import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BadgeCheck, BookOpen, Star, MessageSquare, Globe, ArrowLeft, User, Share2, Link2, Check, Pencil, Instagram, Facebook, Code2 } from 'lucide-react';
import { getCoverUrl, getCoverDisplayUrl, getCoverThumbUrl } from '../utils/coverUrl';
import { EditAuthorProfileDialog } from '../components/EditAuthorProfileDialog';
import { EmbedCodeDialog } from '../components/EmbedCodeDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const socialHref = (val, base) =>
  val.startsWith('http') ? val : `${base}${val.replace(/^@/, '')}`;

export default function AuthorProfile() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);

  const myId = localStorage.getItem('userId');
  const isAdmin = localStorage.getItem('userRole') === 'admin';
  const isOwner = !!(author?.owner_user_id && myId && author.owner_user_id === myId);
  const canEdit = isOwner || (isAdmin && !!author?.owner_user_id);

  const shareUrl = `${BACKEND_URL}/api/authors/${encodeURIComponent(name)}/share`;
  const embedUrl = `${BACKEND_URL}/api/authors/${encodeURIComponent(name)}/embed`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="420" style="border:0;border-radius:12px;max-width:640px" title="${author?.name || name} — SevenChapterBooks author" loading="lazy"></iframe>`;
  const shareText = author ? `Discover books by ${author.name} on SevenChapterBooks` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
        return;
      } catch { /* user dismissed */ }
    } else {
      copyLink();
    }
  };

  const fetchAuthor = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setNotFound(false);
    }
    try {
      const res = await axios.get(`${API}/authors/${encodeURIComponent(name)}`);
      setAuthor(res.data);
    } catch {
      if (!silent) setNotFound(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [name]);

  useEffect(() => { fetchAuthor(); }, [fetchAuthor]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (notFound || !author) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-gray-400 gap-4">
        <User className="h-12 w-12 opacity-40" />
        <p>Author not found.</p>
        <button onClick={() => navigate(-1)} className="text-red-400 hover:text-red-300 text-sm">Go back</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="author-profile-page">
      {/* Hero */}
      <div className="relative border-b border-white/10 overflow-hidden">
        {author.background_url ? (
          <>
            <img
              src={getCoverUrl(author.background_url)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="author-background-photo"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950/30 via-gray-950/60 to-gray-950" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950" />
        )}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-28 pb-10 sm:pt-32 sm:pb-14">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
              data-testid="author-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {canEdit && (
              <button
                onClick={() => setEditOpen(true)}
                className="flex items-center gap-1.5 text-sm font-medium bg-white/10 hover:bg-white/20 border border-white/15 text-white rounded-full px-4 py-2 transition-colors backdrop-blur-sm"
                data-testid="author-edit-profile-btn"
              >
                <Pencil className="h-3.5 w-3.5" /> {isOwner ? 'Edit Profile' : 'Edit Profile (Admin)'}
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-8 items-start">
            {author.photo_url ? (
              <img
                src={getCoverDisplayUrl(author.photo_url)}
                alt={author.name}
                className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl object-cover border border-white/15 shadow-2xl"
                data-testid="author-photo"
              />
            ) : (
              <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <User className="h-14 w-14 text-gray-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-3xl sm:text-4xl font-bold" data-testid="author-name">{author.name}</h1>
                {author.verified && (
                  <span
                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide bg-sky-500/15 text-sky-300 border border-sky-500/40 rounded-full px-2.5 py-1"
                    data-testid="verified-author-badge"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified Author
                  </span>
                )}
              </div>
              <div className="flex items-center gap-5 mt-4 text-sm text-gray-300 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <BookOpen className="h-4 w-4 text-red-400" />
                  {author.book_count} {author.book_count === 1 ? 'book' : 'books'}
                </span>
                {author.average_rating != null && (
                  <span className="flex items-center gap-1.5">
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                    {author.average_rating} avg rating
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4 text-gray-400" />
                  {author.total_reviews} {author.total_reviews === 1 ? 'review' : 'reviews'}
                </span>
              </div>
              {author.bio && (
                <p className="text-gray-300 leading-relaxed mt-5 whitespace-pre-line max-w-2xl" data-testid="author-bio">
                  {author.bio}
                </p>
              )}
              {author.website && (
                <a
                  href={author.website.startsWith('http') ? author.website : `https://${author.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 mt-4 mr-5 transition-colors"
                  data-testid="author-website-link"
                >
                  <Globe className="h-4 w-4" /> Visit website
                </a>
              )}
              {(author.instagram || author.twitter || author.facebook) && (
                <div className="flex items-center gap-3 mt-4" data-testid="author-social-links">
                  {author.instagram && (
                    <a
                      href={socialHref(author.instagram, 'https://instagram.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                      aria-label="Instagram"
                      data-testid="author-instagram-link"
                    >
                      <Instagram className="h-4 w-4 text-gray-200" />
                    </a>
                  )}
                  {author.twitter && (
                    <a
                      href={socialHref(author.twitter, 'https://x.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                      aria-label="X (Twitter)"
                      data-testid="author-twitter-link"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-gray-200"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                    </a>
                  )}
                  {author.facebook && (
                    <a
                      href={socialHref(author.facebook, 'https://facebook.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                      aria-label="Facebook"
                      data-testid="author-facebook-link"
                    >
                      <Facebook className="h-4 w-4 text-gray-200" />
                    </a>
                  )}
                </div>
              )}

              {/* Share this profile */}
              <div className="flex items-center gap-2 mt-6 flex-wrap" data-testid="author-share-row">
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 text-sm font-medium bg-red-600 hover:bg-red-500 text-white rounded-full px-4 py-2 transition-colors"
                  data-testid="author-share-btn"
                >
                  <Share2 className="h-4 w-4" /> Share Profile
                </button>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/15 text-gray-200 rounded-full px-4 py-2 transition-colors"
                  data-testid="author-copy-link-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Link2 className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/15 rounded-full transition-colors"
                  aria-label="Share on X"
                  data-testid="share-x-btn"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-gray-200"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/15 rounded-full transition-colors"
                  aria-label="Share on Facebook"
                  data-testid="share-facebook-btn"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-gray-200"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/15 rounded-full transition-colors"
                  aria-label="Share on WhatsApp"
                  data-testid="share-whatsapp-btn"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-gray-200"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                </a>
                <button
                  onClick={() => setEmbedOpen(true)}
                  className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/15 text-gray-200 rounded-full px-4 py-2 transition-colors"
                  data-testid="author-embed-btn"
                >
                  <Code2 className="h-4 w-4" /> Embed
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Books */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-lg font-semibold mb-6">Books by {author.name}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5" data-testid="author-books-grid">
          {author.books.map((b) => (
            <Link
              key={b.id}
              to={`/book/${b.id}`}
              className="group"
              data-testid={`author-book-${b.id}`}
            >
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-white/5 border border-white/10 group-hover:border-red-500/50 transition-colors">
                <img
                  src={getCoverThumbUrl(b.cover_image_url)}
                  alt={b.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <p className="text-sm font-medium mt-2 line-clamp-2 group-hover:text-red-300 transition-colors">{b.title}</p>
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                {b.rating > 0 && (
                  <>
                    <Star className="h-3 w-3 text-amber-400 fill-amber-400" /> {b.rating}
                  </>
                )}
                {b.publish_year ? <span className="ml-1">{b.publish_year}</span> : null}
              </p>
            </Link>
          ))}
        </div>
      </div>

      <EditAuthorProfileDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={() => fetchAuthor(true)}
        targetUserId={!isOwner && isAdmin ? author.owner_user_id : null}
      />

      <EmbedCodeDialog
        open={embedOpen}
        onOpenChange={setEmbedOpen}
        embedUrl={embedUrl}
        embedCode={embedCode}
        previewTitle={`${author.name} — embed preview`}
        previewHeight={380}
      />
    </div>
  );
}
